// Centralized number/string formatters used across the dashboard.

export const fmt = {
  // 숫자만 반환 — "크레딧" 단위를 셀마다 붙이면 표가 시끄러워서(2026-07-18 피드백) 단위는
  // 칼럼 헤더·라벨·호버가 말하게 하고, 문장 속에서만 호출부가 " 크레딧"을 직접 붙인다.
  nc: (n) => Math.round(Number(n) || 0).toLocaleString("ko-KR"),
  krw: (n) => `₩${Math.round(Number(n) || 0).toLocaleString("ko-KR")}`,
  usd: (n) => `$${(Number(n) || 0).toFixed(4)}`,
  int: (n) => (Math.round(Number(n) || 0)).toLocaleString("ko-KR"),
  pct: (n) => `${((Number(n) || 0) * 100).toFixed(1)}%`,
  compact: (n) => {
    const x = Number(n) || 0;
    if (Math.abs(x) >= 1e8) return `${(x / 1e8).toFixed(1)}억`;
    if (Math.abs(x) >= 1e4) return `${(x / 1e4).toFixed(1)}만`;
    if (Math.abs(x) >= 1e3) return `${(x / 1e3).toFixed(1)}천`;
    return String(x);
  },
  // 모바일 KPI 전용 축약 통화 — 좁은 카드에서 ₩100,088,687 같은 값이 줄바꿈되지 않게
  // ₩1.0억 / ₩9,820만 / ₩120.5만 형태로 줄인다. 전체 정확값은 KPI title(호버/접근성)에 유지.
  // (2026-09-21, 모바일 UX Phase 1)
  //   1억 이상   → X.X억      (예: 100,088,687 → ₩1.0억)
  //   1천만 이상 → N,NNN만    (예:  98,200,000 → ₩9,820만 — 소수점이 오히려 읽기 나빠서 정수)
  //   1만 이상   → X.X만      (예:   1,205,000 → ₩120.5만)
  //   1만 미만   → 정확값     (단위가 작아 오해 소지 없음)
  krwCompact: (n) => {
    const x = Math.round(Number(n) || 0);
    const sign = x < 0 ? "-" : "";
    const a = Math.abs(x);
    if (a >= 1e8) return `${sign}₩${(a / 1e8).toFixed(1)}억`;
    if (a >= 1e7) return `${sign}₩${Math.round(a / 1e4).toLocaleString("ko-KR")}만`;
    if (a >= 1e4) return `${sign}₩${(a / 1e4).toFixed(1)}만`;
    return `₩${x.toLocaleString("ko-KR")}`;
  },
  // 백엔드가 주는 createdAt/lastSeen 은 UTC ISO 문자열. KST(UTC+9, DST 없음)로 고정 변환해 표시.
  // "YYYY-MM-DD HH:MM:SS" 반환 — 분 단위만 필요하면 .slice(0, 16).
  kstDateTime: (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso).slice(0, 19).replace("T", " ");
    const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${kst.getUTCFullYear()}-${pad(kst.getUTCMonth() + 1)}-${pad(kst.getUTCDate())} ${pad(kst.getUTCHours())}:${pad(kst.getUTCMinutes())}:${pad(kst.getUTCSeconds())}`;
  },
};

// 도구 그룹별 색 — ainova.news 본체(ToolComponents.jsx ICON_BADGE)와 동일한 매핑을 그대로
// 재사용(2026-07-15) — 대시보드에서 도구 필터를 색으로 구분할 때 실제 앱과 같은 색이면
// "아, 그 초록색 보도자료 도구" 식으로 바로 연결돼서 인지 부하가 적다.
export const TOOL_GROUP_DOT = {
  title:              "bg-blue-500",
  proofread:          "bg-violet-500",
  press:              "bg-emerald-500",
  foreign:            "bg-rose-500",
  column:             "bg-amber-500",
  buddy:              "bg-orange-500",
  print_title:        "bg-blue-500",
  print_visual:       "bg-blue-500",
  custom_instruction: "bg-indigo-500",
  article_diagnosis:  "bg-indigo-500",
  freechat:           "bg-teal-500",
  imagegen:           "bg-teal-500",
  style_analysis:     "bg-slate-500",
};

// 실제 앱(frontend/src/config/tools.js TEXT_TOOLS[id].name)과 1:1 동일 문구로 통일(2026-07-16) —
// 그동안 "제목 생성"/"외신 번역"/"칼럼 퇴고"/"AI 버디"/"지면 제목"/"이미지 생성(GPT)"처럼 대시보드가
// 임의로 붙인 수식어가 섞여 있어서 실제 앱 화면과 다른 이름으로 보이는 문제가 있었다.
export const TOOL_LABELS = {
  title:        "제목",
  proofread:    "교열",
  press:        "보도자료",
  foreign:      "외신",
  column:       "퇴고",
  buddy:        "버디",
  print_title:  "지면 제목 추천",
  print_visual: "지면 시각물 아이디어",
  custom_instruction: "맞춤지시",
  article_diagnosis:  "기사진단",
  freechat:     "자유 대화",
  imagegen:     "이미지 생성",
  comingsoon01: "벌점 체크",
  comingsoon02: "손배 체크",
  pr_corp:      "기업 PR",
  pr_finance:   "금융 PR",
  pr_public:    "기관 PR",
  pr_gov_join:  "국책참여 PR",
  imagegen_spec: "이미지 스펙 호출 (내부·차감 0)",
  style_analysis: "문체 분석",
  prism:        "PRISM",
  table:        "표 생성",
  chart:        "차트 생성",
  chart_convert: "차트 변환",
  chart_slice:  "차트 슬라이스",
  table_extract: "표 추출",
  infographic:  "인포그래픽",
  image:        "이미지 생성",
  tts:          "TTS",
  podcast:      "팟캐스트",
  chat:         "미분류 (구버전 기록)",
  default:      "일반 대화 (도구 미선택)",
};

export function toolLabel(id) {
  return TOOL_LABELS[id] || id || "(unknown)";
}

// 도구별 엔진(서브타입) 조합 라벨 — frontend/src/config/tools.js 의 subTypes.engine 실값 기준.
// key: `${tool}|${engine}`
export const TOOL_ENGINE_LABELS = {
  "title|t5":                        "온라인_제목",
  "title|c7":                        "창의_제목",
  "proofread|Business":              "경제_교열",
  "proofread|General News":          "사회_교열",
  "press|Government:Public":         "공공_보도",
  "press|Corporate":                 "기업_보도",
  "foreign|English":                 "영어_외신",
  "foreign|Japanese":                "일어_외신",
  "column|Structure Analysis":       "장문_퇴고",
  "column|Quick Edit":               "단문_퇴고",
  "buddy|Brief Buddy":               "일보_작성",
  "buddy|Article Buddy":             "기사_작성",
  "print_title|자동판단":              "지면_제목",
  "print_visual|인포그래픽":           "시각_기획",
  "custom_instruction|개인화":         "맞춤_지시",
  "article_diagnosis|기사진단":        "기사_진단",
  "freechat|default":                 "자유_대화",
  "imagegen|default":                 "이미지_생성",
  // 2026-08-12(A-3): nova 실서비스에는 있었는데 이 목록에만 빠져 있던 실도구 6종.
  // 백엔드 KNOWN_TOOL_ENGINES 와 짝이 맞아야 한다(tests/test_tool_engine_registry.py).
  "comingsoon01|default":             "벌점_체크",
  "comingsoon02|default":             "손배_체크",
  "pr_corp|default":                  "기업_PR",
  "pr_finance|default":               "금융_PR",
  "pr_public|default":                "기관_PR",
  "pr_gov_join|default":              "국책참여_PR",
  // style_analysis 는 credit_hook.record_style_analysis_usage() 가 engine 없이("") 남기는
  // MyStyle 문체 분석 호출 — 2026-07-16, "도구별 사용량"에 usage 와 함께 포함시키며 등록.
  "style_analysis|":                  "문체_분석",
};

// 채팅형(서브타입이 없는) tool — engine 값이 붙어있어도 의미가 없어 항상 무시한다.
const NO_ENGINE_TOOLS = new Set(["default", "chat"]);

// "미선택" 칩 전용 sentinel. 백엔드 UNMAPPED_SENTINEL 과 동일하게 유지할 것.
export const UNMAPPED_ENGINE = "__unmapped__";

export function toolEngineLabel(tool, engine) {
  if (engine === UNMAPPED_ENGINE) return "미선택";
  const key = `${tool}|${engine || ""}`;
  if (TOOL_ENGINE_LABELS[key]) return TOOL_ENGINE_LABELS[key];
  const base = toolLabel(tool);
  if (NO_ENGINE_TOOLS.has(tool)) return base;
  if (engine) return `${base}_${engine}`;
  // engine 미기록(계측 전 옛 로그): 그 도구의 정식 조합이 하나뿐이면 그 서비스 표기로
  // 귀속(예: print_visual → 시각_기획) — "(엔진 미선택)" 꼬리표는 관리자에게 소음이고
  // 같은 기능이 두 이름으로 보이게 만들었다(2026-07-18 피드백). 다중 엔진 도구는
  // 어느 엔진인지 알 수 없으니 도구명만.
  const entries = Object.entries(TOOL_ENGINE_LABELS).filter(([k]) => k.startsWith(`${tool}|`));
  if (entries.length === 1) return entries[0][1];
  return base;
}

// 필터 칩 목록에 노출할지 여부 — TOOL_ENGINE_LABELS 에 정의된 정상 조합만 보여준다
// (engine 미기록·미분류 채팅 등 로그 결측 케이스는 칩에서 제외).
export function hasToolEngineLabel(tool, engine) {
  return Boolean(TOOL_ENGINE_LABELS[`${tool}|${engine || ""}`]);
}

// 도구 그룹 순서 — frontend/src/config/tools.js TEXT_TOOLS 의 실제 순서(=앱 그리드 1~5열 순서:
// 버디+교열 · 제목+지면제목+지면비주얼 · 보도+외신 · 퇴고+맞춤지시+기사진단 · 자유대화+이미지생성)와
// 1:1 동일하게 맞춤(2026-07-16) — 예전엔 임의 순서라 앱 화면과 대시보드 순서가 달라 헷갈렸음.
// 매핑 안 된 tool(예: default)은 맨 뒤로.
// style_analysis 는 tools.js 그리드에 없는(도구 선택 화면이 아니라 MyStyle 설정에서 트리거되는)
// 별도 기능이라 그리드 순서 기준이 없다 — 맨 뒤에 추가(2026-07-16).
export const TOOL_GROUP_ORDER = [
  "buddy", "proofread", "title", "print_title", "print_visual",
  "press", "foreign", "column", "custom_instruction", "article_diagnosis",
  "freechat", "imagegen", "style_analysis",
  // 2026-08-12(A-3) — 필터 트리에 나오려면 여기에도 있어야 한다(SUPER_GROUP_ORDER 재료).
  "comingsoon01", "comingsoon02",
  "pr_corp", "pr_finance", "pr_public", "pr_gov_join",
];

// 엔진이 1개뿐인 도구끼리는 "도구별 사용량" 표/차트에서 단독 1칸으로 두지 않고 이웃 도구와
// 묶는다(2026-07-16 피드백) — 지면 제목 추천+지면 시각물 아이디어, 맞춤지시+기사진단,
// 자유 대화+이미지 생성. TOOL_GROUP_ORDER 상에서 이미 서로 인접해 있어 병합이 자연스럽다.
// 나머지 6개 도구(버디/교열/제목/보도/외신/퇴고)는 이미 엔진이 2개씩이라 그대로 자기 자신이 그룹.
export const TOOL_SUPER_GROUP = {
  print_title: "print", print_visual: "print",
  custom_instruction: "assist", article_diagnosis: "assist",
  freechat: "freeform", imagegen: "freeform",
  // PR 4종은 성격이 같아 한 그룹으로 접는다(2026-08-12) — 필터 트리에서 칩 4개가
  // 최상위에 흩어지면 도구 목록이 산만해진다. 벌점/손배 체크는 서로 다른 업무라 각자 그룹.
  pr_corp: "pr", pr_finance: "pr", pr_public: "pr", pr_gov_join: "pr",
};
export function superGroupOf(tool) {
  return TOOL_SUPER_GROUP[tool] || tool;
}
const SUPER_GROUP_LABELS = {
  pr: "PR 보도자료",
  print: "지면",
  assist: "맞춤지시·기사진단",
  freeform: "자유대화·이미지생성",
};
export function superGroupLabel(superGroupId) {
  return SUPER_GROUP_LABELS[superGroupId] || TOOL_LABELS[superGroupId] || superGroupId;
}

// TOOL_GROUP_ORDER(12개 tool) 를 슈퍼그룹 단위로 접은 순서(10개) — "도구별 사용량"의 기본
// 축소 뷰(그룹당 라인/컬럼 1개)와 필터 트리 렌더링에 사용.
export const SUPER_GROUP_ORDER = (() => {
  const seen = new Set();
  const order = [];
  for (const tool of TOOL_GROUP_ORDER) {
    const sg = superGroupOf(tool);
    if (!seen.has(sg)) { seen.add(sg); order.push(sg); }
  }
  // 가나다순(2026-07-18 피드백) — 차트 범례·표 열·필터 트리가 전부 이 순서를 쓰므로
  // 여기 한 곳만 정렬하면 화면 전체의 그룹 순서가 일치한다. 임의 순서는 "차트는 교열이
  // 먼저인데 표는 버디가 먼저"처럼 화면마다 달라 보여 헷갈렸음.
  return order.sort((a, b) => superGroupLabel(a).localeCompare(superGroupLabel(b), "ko"));
})();

// 도구 그룹(9개: 위 병합 이후 실제 표시 그룹 수)별 추이 차트 라인 색. 모든 그룹이 정확히
// 엔진 2개씩을 갖게 되므로(병합 전엔 6개는 2엔진·6개는 1엔진이라 들쭉날쭉했음) 그룹당
// 기본색 1개 + 두 번째 엔진용 밝은 변형(-300) 1개만 있으면 충분하다.
export const TOOL_LINE_COLORS = {
  buddy:     "#f97316", // orange-500
  proofread: "#8b5cf6", // violet-500
  title:     "#3b82f6", // blue-500
  print:     "#0ea5e9", // sky-500
  press:     "#10b981", // emerald-500
  foreign:   "#f43f5e", // rose-500
  column:    "#f59e0b", // amber-500
  assist:    "#6366f1", // indigo-500
  freeform:  "#14b8a6", // teal-500
  style_analysis: "#64748b", // slate-500 — 엔진 1개뿐이라 밝은 변형 불필요
};
const TOOL_LINE_COLORS_LIGHT = {
  buddy:     "#fdba74", // orange-300
  proofread: "#c4b5fd", // violet-300
  title:     "#93c5fd", // blue-300
  print:     "#7dd3fc", // sky-300
  press:     "#6ee7b7", // emerald-300
  foreign:   "#fda4af", // rose-300
  column:    "#fcd34d", // amber-300
  assist:    "#a5b4fc", // indigo-300
  freeform:  "#5eead4", // teal-300
};

// TOOL_ENGINE_LABELS 에 정의된 순서로 같은 그룹 내 세부 정렬. 매핑 안 된 engine(엔진 미선택)은 그룹 내 맨 뒤로.
const TOOL_ENGINE_ORDER = Object.keys(TOOL_ENGINE_LABELS);

// 도구+엔진 조합의 차트 라인 색 — 병합된 슈퍼그룹 안에서 몇 번째 엔진인지에 따라 기본색/밝은색을 고른다.
export function engineLineColor(tool, engine) {
  const sg = superGroupOf(tool);
  const keysInGroup = TOOL_ENGINE_ORDER.filter((k) => superGroupOf(k.split("|")[0]) === sg);
  const idx = keysInGroup.indexOf(`${tool}|${engine || ""}`);
  if (idx === 1 && TOOL_LINE_COLORS_LIGHT[sg]) return TOOL_LINE_COLORS_LIGHT[sg];
  return TOOL_LINE_COLORS[sg] || "#94a3b8";
}

export function sortToolEngines(list) {
  return [...list].sort((a, b) => {
    const ga = TOOL_GROUP_ORDER.indexOf(a.tool);
    const gb = TOOL_GROUP_ORDER.indexOf(b.tool);
    const groupDiff = (ga === -1 ? 999 : ga) - (gb === -1 ? 999 : gb);
    if (groupDiff !== 0) return groupDiff;
    const ia = TOOL_ENGINE_ORDER.indexOf(`${a.tool}|${a.engine || ""}`);
    const ib = TOOL_ENGINE_ORDER.indexOf(`${b.tool}|${b.engine || ""}`);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
}

export const TIER_COLORS = {
  fable:  "#be185d",
  opus:   "#7c3aed",
  sonnet: "#f17c14",
  haiku:  "#0ea5e9",
  nova:   "#22c55e",
};

// 백엔드 modelId 원본("us.anthropic.claude-opus-4-8", "us.anthropic.claude-opus-4-6-v1" 등)을
// "Opus 4.8" 처럼 사람이 읽을 수 있는 이름으로 — 신규 모델이 계속 추가되는 걸 감안해
// 버전별 하드코딩 매핑 대신 CRIS 프리픽스(us./global./apac.)와 "anthropic.claude-"를 벗기고
// 티어명+숫자 버전만 남기는 정규식 파싱으로 처리한다(2026-07-17, "Fable/Sonnet 5.0처럼
// 모델별로 보고 싶다" 피드백).
// modelId → 티어(fable/opus/sonnet/haiku/nova) — 백엔드 detect_tier 와 동일 규칙.
// TIER_COLORS 키와 짝을 이뤄 모델별 차트/표의 색을 고를 때 쓴다.
export function tierOfModel(modelId) {
  const m = (modelId || "").toLowerCase();
  if (m.includes("fable")) return "fable";
  if (m.includes("opus")) return "opus";
  if (m.includes("haiku")) return "haiku";
  if (m.includes("sonnet")) return "sonnet";
  if (m.includes("nova")) return "nova";
  return "sonnet";
}

// 실청구와 대조할 소스가 없는 외부 원가의 modelId.
// 백엔드 novadb/backend/analytics/pricing.py 의 UNTRACKED_EXTERNAL_MODEL_IDS 와 같은 집합이며,
// 그쪽 주석에 왜 이 구분이 필요한지 적혀 있다. 한쪽만 고치면 화면과 API 가 어긋난다.
export const UNTRACKED_EXTERNAL_MODEL_IDS = new Set(["exa"]);

// Bedrock·OpenAI 추정치는 BillingON·OpenAI Costs API 로 맞춰볼 수 있지만 이건 맞춰볼
// 대상이 없다. 표에서 같은 "(추정)" 꼬리표를 달면 그 차이가 지워진다.
export function isUntrackedExternal(modelId) {
  return UNTRACKED_EXTERNAL_MODEL_IDS.has(modelId);
}

export function prettyModelId(modelId) {
  if (!modelId || modelId === "n/a") return "기타";
  if (modelId === "imagegen") return "이미지 생성(GPT)";
  // Exa 는 LLM 이 아니라 검색 API 다 — 모델 목록에 "Exa" 로 끼면 모델처럼 읽힌다.
  if (modelId === "exa") return "Exa 검색 API";
  const stripped = modelId.replace(/^(us|global|apac)\./, "").replace(/^anthropic\.claude-/, "");
  const parts = stripped.split("-");
  if (parts.length === 0 || !parts[0]) return modelId;
  const tier = parts[0][0].toUpperCase() + parts[0].slice(1);
  const versionParts = [];
  for (let i = 1; i < parts.length; i++) {
    if (/^\d+$/.test(parts[i])) versionParts.push(parts[i]);
    else break;
  }
  return versionParts.length ? `${tier} ${versionParts.join(".")}` : tier;
}
